import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, BarChart3, Shield, Users, RefreshCw, AlertTriangle } from 'lucide-react';

function KpiCard({ label, value, icon: Icon }) {
  return (
    <Card className="border"><CardContent className="p-3 flex items-center gap-3">
      {Icon && <div className="p-2 rounded-lg bg-muted/40"><Icon className="h-4 w-4 text-muted-foreground" /></div>}
      <div><div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div><div className="text-lg font-bold">{value}</div></div>
    </CardContent></Card>
  );
}

export default function QualityDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [byMeasure, setByMeasure] = useState(null);
  const [byProvider, setByProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes, pRes] = await Promise.all([
        api.get('/quality/dashboard/summary'),
        api.get('/quality/dashboard/by-measure'),
        api.get('/quality/dashboard/by-provider'),
      ]);
      setSummary(sRes.data);
      setByMeasure(mRes.data.by_measure || {});
      setByProvider(pRes.data.by_provider || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex-1 p-4 space-y-3"><Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full" /></div>;

  return (
    <div className="flex-1 overflow-auto p-4 space-y-4 max-w-6xl" data-testid="quality-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5" />Quality Dashboard</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => navigate('/quality-admin')} data-testid="goto-quality-admin"><Shield className="h-3 w-3" />Settings</Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fetch}><RefreshCw className="h-3 w-3" /></Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Patients Evaluated" value={summary.patients_evaluated} icon={Users} />
          <KpiCard label="Open Gaps" value={summary.open_gaps} icon={AlertTriangle} />
          <KpiCard label="Compliant" value={summary.compliant} icon={Shield} />
          <KpiCard label="Declined" value={summary.declined} />
          <KpiCard label="Actions Taken" value={summary.actions_taken} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border">
          <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Gaps by Measure</CardTitle></CardHeader>
          <CardContent className="p-4">
            {byMeasure && Object.entries(byMeasure).map(([mid, statuses]) => (
              <div key={mid} className="py-1.5 border-b last:border-0">
                <div className="text-xs font-medium">{mid.replace(/qm_/g, '').replace(/_/g, ' ')}</div>
                <div className="flex gap-3 mt-0.5">
                  {Object.entries(statuses).map(([st, cnt]) => (
                    <span key={st} className="text-[10px]"><span className={st === 'overdue' ? 'text-red-600' : st === 'due' ? 'text-amber-600' : st === 'compliant' ? 'text-emerald-600' : 'text-muted-foreground'}>{st}</span>: {cnt}</span>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border">
          <CardHeader className="py-2 px-4 border-b"><CardTitle className="text-sm">Actions by Provider</CardTitle></CardHeader>
          <CardContent className="p-4">
            {byProvider?.length ? byProvider.map(p => (
              <div key={p.user_id} className="flex justify-between py-1 border-b last:border-0 text-xs">
                <span className="font-mono">{(p.user_id || '').slice(0, 12)}...</span>
                <span className="font-medium">{p.action_count} actions</span>
              </div>
            )) : <p className="text-xs text-muted-foreground">No provider actions yet.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
